import { useState, useEffect } from 'react';

/**
 * Custom hook to debounce a value.
 * This hook delays updating the value until after a specified delay period has passed since the last change to the value.
 * This is useful for reducing the frequency of updates, such as in search inputs or API calls.
 * @param value The value to debounce.
 * @param delay The delay in milliseconds to wait before updating the value.
 * @returns The debounced value.
 */

const useDebounce = <T>(value: T, delay: number): T => {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        // Set a timeout to update the debounced value
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        // Cleanup function to clear the timeout if value changes before delay
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

export default useDebounce;
